import { setActiveEntityId } from "@/lib/api"
import type { Entity } from "@/lib/entities"

/**
 * Que sociedad esta mirando el usuario, y como sobrevive a una recarga.
 *
 * Es el mismo problema que el tenant (`cfo.activeTenantId` en `auth.tsx`) una
 * capa mas abajo, y se resuelve igual — con una diferencia deliberada en la
 * forma de guardarlo:
 *
 *   tenant   ->  "cfo.activeTenantId"  = "<uuid>"
 *   entidad  ->  "cfo.activeEntity"    = {"tenantId": "<uuid>", "entityId": "<uuid>"}
 *
 * La entidad se guarda JUNTO al tenant al que pertenece porque un `entity_id`
 * suelto no significa nada: una sociedad de otro cliente del despacho no es un
 * valor "viejo", es un valor de otra persona. Guardandolos juntos, el reseteo
 * al cambiar de cliente no es un efecto que alguien tenga que acordarse de
 * escribir — es una consecuencia de la forma del dato: si el tenant guardado no
 * es el actual, no hay entidad guardada. Punto.
 *
 * Todo lo de este archivo es funcion pura o toca solo localStorage, para poder
 * probarlo sin montar React (ver `activeEntity.test.ts`).
 */

export const ACTIVE_ENTITY_KEY = "cfo.activeEntity"

interface EntidadGuardada {
  tenantId: string
  entityId: string
}

/** localStorage puede no existir (SSR, tests en node) o estar bloqueado. */
function almacen(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/**
 * La entidad guardada para ESTE tenant, o null.
 *
 * Devuelve null —no la guardada— cuando el tenant no coincide: es el reseteo
 * al cambiar de cliente.
 */
export function leerEntidadGuardada(tenantId: string | null): string | null {
  if (!tenantId) return null
  const crudo = almacen()?.getItem(ACTIVE_ENTITY_KEY)
  if (!crudo) return null
  try {
    const guardada = JSON.parse(crudo) as Partial<EntidadGuardada>
    if (!guardada?.tenantId || !guardada?.entityId) return null
    return guardada.tenantId === tenantId ? guardada.entityId : null
  } catch {
    // Un valor corrupto no puede tumbar el arranque de la sesion.
    return null
  }
}

export function guardarEntidad(tenantId: string | null, entityId: string | null) {
  const storage = almacen()
  if (!storage) return
  if (!tenantId || !entityId) {
    storage.removeItem(ACTIVE_ENTITY_KEY)
    return
  }
  storage.setItem(ACTIVE_ENTITY_KEY, JSON.stringify({ tenantId, entityId }))
}

/**
 * Que sociedad corresponde mostrar, dadas las visibles y la que venia guardada.
 *
 * El orden de las reglas es la decision de producto:
 *
 * 1. Sin tenant o sin sociedades visibles: no hay nada que elegir.
 * 2. La guardada, si sigue siendo visible. Recargar la pagina no debe cambiar
 *    de sociedad sin avisar.
 * 3. Una sola sociedad: se elige sola. Es el caso de la mayoria de los
 *    clientes, y hacer elegir cuando no hay eleccion es solo un paso de mas.
 * 4. Varias y ninguna guardada: NULL a proposito. No se elige "la primera" —
 *    en un producto contable, mirar los numeros de la sociedad equivocada es un
 *    error caro y silencioso, y una preseleccion arbitraria lo vuelve probable.
 *    La pantalla lo pide de forma explicita.
 */
export function resolverEntidadActiva(opciones: {
  tenantId: string | null
  entities: Entity[]
  guardada: string | null
}): string | null {
  const { tenantId, entities, guardada } = opciones
  if (!tenantId || entities.length === 0) return null
  if (guardada && entities.some((e) => e.id === guardada)) return guardada
  if (entities.length === 1) return entities[0].id
  return null
}

/**
 * Resuelve, persiste y publica la entidad activa. Es lo que llama el
 * `AuthProvider` cada vez que cambia el tenant o la lista de sociedades.
 *
 * Devuelve el id resuelto para que el estado de React quede en fase con lo que
 * ya viaja en las peticiones.
 */
export function aplicarEntidadActiva(tenantId: string | null, entities: Entity[]): string | null {
  const resuelta = resolverEntidadActiva({
    tenantId,
    entities,
    guardada: leerEntidadGuardada(tenantId),
  })
  guardarEntidad(tenantId, resuelta)
  setActiveEntityId(resuelta)
  return resuelta
}

/** Eleccion explicita del usuario en el selector de la cabecera. */
export function elegirEntidad(tenantId: string | null, entityId: string | null) {
  guardarEntidad(tenantId, entityId)
  setActiveEntityId(entityId)
}
